/**
 * ConfirmDialog Component
 * Confirmation modal for destructive actions
 */

import { Modal } from '@/components/base';
import { AlertTriangle, Info, CheckCircle } from 'lucide-react';

interface ConfirmDialogProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string;
    variant?: 'danger' | 'warning' | 'info' | 'success';
    loading?: boolean;
    /** Extra controls between the message and the buttons (an optional note field, say). */
    children?: React.ReactNode;
}

export function ConfirmDialog({
    isOpen,
    onClose,
    onConfirm,
    title,
    message,
    confirmText = 'Confirm',
    cancelText = 'Cancel',
    variant = 'danger',
    loading = false,
    children,
}: ConfirmDialogProps) {
    const variantConfig = {
        danger: {
            icon: <AlertTriangle className="w-12 h-12 text-red-500" />,
            buttonClass: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
        },
        warning: {
            icon: <AlertTriangle className="w-12 h-12 text-amber-500" />,
            buttonClass: 'bg-amber-600 hover:bg-amber-700 focus:ring-amber-500',
        },
        info: {
            icon: <Info className="w-12 h-12 text-blue-500" />,
            buttonClass: 'bg-blue-600 hover:bg-blue-700 focus:ring-blue-500',
        },
        success: {
            icon: <CheckCircle className="w-12 h-12 text-green-500" />,
            buttonClass: 'bg-green-600 hover:bg-green-700 focus:ring-green-500',
        },
    };

    const config = variantConfig[variant];

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="sm" showCloseButton={false}>
            <div className="text-center py-4">
                <div className="mx-auto mb-4">{config.icon}</div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
                <p className={`text-gray-600 ${children ? 'mb-4' : 'mb-6'}`}>{message}</p>
                {children && <div className="mb-6 text-left">{children}</div>}
                <div className="flex justify-center gap-3">
                    <button
                        onClick={onClose}
                        disabled={loading}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                    >
                        {cancelText}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        className={`px-4 py-2 text-white rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 ${config.buttonClass}`}
                    >
                        {loading ? 'Processing...' : confirmText}
                    </button>
                </div>
            </div>
        </Modal>
    );
}

export default ConfirmDialog;
